import {
  Accordion,
  ActionMenu,
  Avatar,
  Box,
  Button,
  Card,
  IconButton,
  IconCheckmark,
  IconChevronDown,
  IconChevronUp,
  IconCrossLarge,
  IconEdit,
  IconEllipses,
  IconExclamationTriangle,
  IconLogoResyGda,
  IconPlusLarge,
  IconStarFilled,
  IconSync,
  IconTrash,
  PageHeader,
  Tag,
  Text,
  tokens,
} from '@tocktix/pantry';
import { AdminUser } from 'tools/bff/generated/bff/admin/admin_user_pb';
import { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { IconBell, IconForkKnife, IconPerson } from '@tock/icons';
import { TockBirthday } from 'tools/bff/generated/tock/patron_pb';
import { useNavigate, useParams } from 'react-router-v7';
import { userClient } from 'tools/bff/client';
import DeactivateUserModal from 'tools/users/DeactivateUserModal';
import EditUserDetailsModal from 'tools/users/EditUserDetailsModal';
import NotifyHistorySection from 'tools/users/NotifyHistorySection';
import ReactivateUserModal from 'tools/users/ReactivateUserModal';
import ReservationHistorySection from 'tools/users/ReservationHistorySection';
import UserHistorySection from 'tools/users/UserHistorySection';

/** Renders a Verified / Not Verified tag based on a boolean. */
const VerificationTag = ({ verified }: { verified: boolean }) => (
  <Tag
    label={verified ? 'Verified' : 'Not Verified'}
    color={verified ? 'success' : 'danger'}
    size="sm"
    IconStart={verified ? IconCheckmark : IconExclamationTriangle}
  />
);

/**
 * A labeled row used within profile cards to identify the data source and
 * optionally show a verification or status tag alongside the value.
 */
const ProfileRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box display="flex" gap={100} alignItems="flexStart">
    <Text variant="bodyStrong" style={{ minWidth: '44px' }}>
      {label}:
    </Text>
    <Box display="flex" flexWrap="wrap" alignItems="center" gap={100}>
      {children}
    </Box>
  </Box>
);

/** Formats a TockBirthday struct as MM/DD/YYYY (or MM/DD if year is absent). */
const formatTockBirthday = (birthday?: TockBirthday): string => {
  if (!birthday || birthday.month == null) {
    return '\u2014';
  }
  const month = String(birthday.month).padStart(2, '0');
  const day = birthday.day != null ? String(birthday.day).padStart(2, '0') : '??';
  return birthday.year != null ? `${month}/${day}/${birthday.year}` : `${month}/${day}`;
};

const UserDetailsRoute: FunctionComponent = () => {
  // Route param name matches the `:id` segment defined in root.tsx
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openedPanels, setOpenedPanels] = useState<Set<string>>(new Set());

  useEffect(() => {
    document.querySelector<HTMLElement>('.Content')?.scrollTo(0, 0);
  }, []);

  // Fetch full enriched profile whenever the route id changes.
  useEffect(() => {
    if (!id) {
      return;
    }

    let isMounted = true;
    const loadUser = async () => {
      setIsLoading(true);
      setError(null);
      setUser(null);

      try {
        const res = await userClient.getUserProfile({ gdusUuid: id });
        if (isMounted) {
          setUser(res.adminUser ?? null);
        }
      } catch {
        if (isMounted) {
          setError('Failed to load user details.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadUser();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const fetchUserProfile = () => {
    return userClient
      .getUserProfile({ gdusUuid: id })
      .then((res) => setUser(res.adminUser ?? null));
  };

  const handleSyncAccount = () => {
    setIsSyncing(true);
    userClient
      .syncUser({ uuid: id })
      .then(fetchUserProfile)
      .catch((reason) => {
        setError('Error syncing account');
        console.error('Error syncing account: ' + reason);
      })
      .finally(() => setIsSyncing(false));
  };

  if (isLoading) {
    return (
      <Box padding={400}>
        <Text>Loading user details...</Text>
      </Box>
    );
  }

  if (isSyncing) {
    return (
      <Box padding={400}>
        <Text>Syncing user details...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={400}>
        <Text>{error}</Text>
        <Button label="Back to search" onClick={() => navigate('/users')} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box padding={400}>
        <Text>User not found.</Text>
        <Button label="Back to search" onClick={() => navigate('/users')} />
      </Box>
    );
  }

  const fullName =
    [user.gdusUser?.firstName, user.gdusUser?.lastName].filter(Boolean).join(' ') || 'Unknown User';

  // TODO: isActive and isGdaMember will come from the API once added to the proto
  const isActive = true;
  const isGdaMember = user.resyUser?.exclusiveGroups?.includes(1000) ?? false;

  return (
    <Box padding={[400, 500]}>
      {/* Profile header card */}
      <PageHeader title={`${fullName}`} description="Details" />
      <Card style={{ boxShadow: tokens.shadow[100] }}>
        <Card.Header
          border
          startElement={
            <Box display="flex" alignItems="center" gap={200}>
              <Avatar gda={isGdaMember} size="md" />
              <Box display="flex" flexDirection="column" gap={50}>
                <Text variant="bodyLgStrong">{fullName}</Text>
                {isGdaMember && (
                  <Box display="flex" alignItems="center" gap={100}>
                    <IconLogoResyGda size={200} />
                    <Text variant="bodySm">Global Dining Access Member</Text>
                  </Box>
                )}
              </Box>
            </Box>
          }
          endElement={
            <Box display="flex" gap={100}>
              <IconButton
                aria-label="Edit user"
                Icon={IconEdit}
                variant="text"
                onClick={() => setIsEditModalOpen(true)}
              />
              <ActionMenu
                id="user-actions-menu"
                trigger={
                  <IconButton aria-label="More options" Icon={IconEllipses} variant="text" />
                }
                direction="bottomEnd"
              >
                <ActionMenu.Item label="Sync Account" Icon={IconSync} onClick={handleSyncAccount} />
                <ActionMenu.Item
                  label="Deactivate Account"
                  Icon={IconCrossLarge}
                  onClick={() => setIsDeactivateModalOpen(true)}
                  disabled={user.gdusUser?.isLockedOut}
                />
                <ActionMenu.Item
                  label="Reactivate Account"
                  Icon={IconSync}
                  onClick={() => setIsReactivateModalOpen(true)}
                  disabled={!user.gdusUser?.isLockedOut}
                />
                <ActionMenu.Item label="Delete Account" Icon={IconTrash} variant="danger" />
                <ActionMenu.Item label="Delete Account (GDPR)" Icon={IconTrash} variant="danger" />
                <ActionMenu.Item label="Retry GDA Eligibility" Icon={IconLogoResyGda} />
                <ActionMenu.Item label="Add to Suspected Bots" Icon={IconPlusLarge} />
              </ActionMenu>
            </Box>
          }
        />
        <Card.Body>
          <Box display="flex" flexDirection="column" gap={200}>
            <Box>
              <Tag
                label={isActive ? 'Is active' : 'Deactivated'}
                color={isActive ? 'primary' : 'secondary'}
              />
            </Box>
            <Box display="flex" flexDirection="column" gap={100}>
              <Box display="flex" gap={100}>
                <Text variant="bodyStrong">GDUS ID:</Text>
                <Text variant="body">{user.gdusUser?.gdusUuid ?? '\u2014'}</Text>
              </Box>
              <Box display="flex" gap={100}>
                <Text variant="bodyStrong">Resy User ID:</Text>
                <Text variant="body">{user.gdusUser?.resyUserId?.toString() ?? '\u2014'}</Text>
              </Box>
              <Box display="flex" gap={100}>
                <Text variant="bodyStrong">Tock Patron ID:</Text>
                <Text variant="body">{user.gdusUser?.tockPatronId?.toString() ?? '\u2014'}</Text>
              </Box>
            </Box>
          </Box>
        </Card.Body>
      </Card>

      {/* Data cards */}
      <Box display="flex" flexDirection="column" gap={200} style={{ marginTop: '20px' }}>
        {/* Phone Number */}
        <Card>
          <Card.Header border title="Phone Number" />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap={100}>
              <ProfileRow label="GDUS">
                <Text variant="body">
                  {user.gdusUser?.phoneNumber ? String(user.gdusUser.phoneNumber) : '\u2014'}
                </Text>
              </ProfileRow>
              <ProfileRow label="Resy">
                <Text variant="body">{user.resyUser?.mobileNumber ?? '\u2014'}</Text>
                {user.resyUser?.mobileNumber != null && (
                  <VerificationTag verified={user.resyUser.mobileNumberIsVerified ?? false} />
                )}
              </ProfileRow>
              <ProfileRow label="Tock">
                <Text variant="body">{user.tockPatron?.phone ?? '\u2014'}</Text>
              </ProfileRow>
            </Box>
          </Card.Body>
        </Card>

        {/* Email */}
        <Card>
          <Card.Header border title="Email" />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap={100}>
              <ProfileRow label="GDUS">
                <Text variant="body">{user.gdusUser?.email ?? '\u2014'}</Text>
              </ProfileRow>
              <ProfileRow label="Resy">
                <Text variant="body">{user.resyUser?.emAddress ?? '\u2014'}</Text>
                {user.resyUser?.emAddress != null && (
                  <VerificationTag verified={user.resyUser.emIsVerified ?? false} />
                )}
              </ProfileRow>
              <ProfileRow label="Tock">
                <Text variant="body">{user.tockPatron?.email ?? '\u2014'}</Text>
                {user.tockPatron?.status != null && (
                  <VerificationTag verified={user.tockPatron.status === 'ACTIVE'} />
                )}
              </ProfileRow>
            </Box>
          </Card.Body>
        </Card>

        {/* Bio */}
        <Card>
          <Card.Header border title="Biography" />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap={100}>
              <ProfileRow label="Resy">
                <Text variant="body">{user.resyUser?.bio ?? '\u2014'}</Text>
              </ProfileRow>
              <ProfileRow label="Tock">
                <Text variant="body">{user.tockPatron?.shortBio ?? '\u2014'}</Text>
              </ProfileRow>
            </Box>
          </Card.Body>
        </Card>

        {/* Date of Birth */}
        <Card>
          <Card.Header border title="Date of Birth" />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap={100}>
              <ProfileRow label="Resy">
                <Text variant="body">{user.resyUser?.dateOfBirth ?? '\u2014'}</Text>
              </ProfileRow>
              <ProfileRow label="Tock">
                <Text variant="body">{formatTockBirthday(user.tockPatron?.birthday)}</Text>
              </ProfileRow>
            </Box>
          </Card.Body>
        </Card>

        {/* Allergies & Restrictions */}
        <Card>
          <Card.Header border title="Allergies &amp; Restrictions" />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap={100}>
              <ProfileRow label="Resy">
                {user.resyUser?.dietaryTags && user.resyUser.dietaryTags.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={100}>
                    {user.resyUser.dietaryTags.map((tag) => (
                      <Tag key={tag} label={tag} color="secondary" size="sm" />
                    ))}
                  </Box>
                ) : (
                  <Text variant="body">{'\u2014'}</Text>
                )}
              </ProfileRow>
              <ProfileRow label="Tock">
                <Box display="flex" flexDirection="column" gap={50}>
                  {user.tockPatron?.dietaryRestrictions ? (
                    <Text variant="body">{user.tockPatron.dietaryRestrictions}</Text>
                  ) : null}
                  {user.tockPatron?.foodAversions ? (
                    <Text variant="body">{user.tockPatron.foodAversions}</Text>
                  ) : null}
                  {!user.tockPatron?.dietaryRestrictions && !user.tockPatron?.foodAversions && (
                    <Text variant="body">{'\u2014'}</Text>
                  )}
                </Box>
              </ProfileRow>
            </Box>
          </Card.Body>
        </Card>

        {/* Concierge Status */}
        <Card>
          <Card.Header border title="Concierge Status" />
          <Card.Body>
            <ProfileRow label="Tock">
              {user.tockPatron?.canBookForGuest != null ? (
                <Tag
                  label={user.tockPatron.canBookForGuest ? 'Eligible' : 'Not Eligible'}
                  color={user.tockPatron.canBookForGuest ? 'success' : 'secondary'}
                  size="sm"
                />
              ) : (
                <Text variant="body">{'\u2014'}</Text>
              )}
            </ProfileRow>
          </Card.Body>
        </Card>

        {/* Payment Methods */}
        <Card>
          <Card.Header border title="Payment Methods" />
          <Card.Body>
            <ProfileRow label="Resy">
              {user.resyUser?.paymentMethods && user.resyUser.paymentMethods.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={100}>
                  {user.resyUser.paymentMethods.map((method, index) => (
                    <Box
                      key={method.id != null ? String(method.id) : index}
                      display="flex"
                      alignItems="center"
                      gap={100}
                    >
                      <Text variant="body">
                        {[method.providerName, method.display].filter(Boolean).join(' \u2014 ') ||
                          '\u2014'}
                      </Text>
                      {method.isDefault && (
                        <Tag label="Default" color="primary" size="sm" IconStart={IconStarFilled} />
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Text variant="body">{'\u2014'}</Text>
              )}
            </ProfileRow>
          </Card.Body>
        </Card>

        {/* American Express User Group */}
        <Card>
          <Card.Header border title="American Express User Group" />
          <Card.Body>
            <Text>{'\u2014'}</Text>
          </Card.Body>
        </Card>

        {/* Reservation History */}
        <Accordion
          onChange={(id, isOpen) => {
            if (isOpen) {
              setOpenedPanels((prev) => new Set(prev).add(id));
            }
          }}
          itemProps={{
            IconClosed: IconChevronDown,
            IconOpen: IconChevronUp,
            iconPosition: 'end',
          }}
        >
          <Accordion.Item id="user-history" title="User History" startElement={<IconPerson />}>
            <UserHistorySection
              gdusUuid={user.gdusUser?.gdusUuid}
              enabled={openedPanels.has('user-history')}
            />
          </Accordion.Item>
          <Accordion.Item
            id="reservation-history"
            title="Reservation History"
            startElement={<IconForkKnife />}
          >
            <ReservationHistorySection
              tockPatronId={user.gdusUser?.tockPatronId}
              resyUserId={user.gdusUser?.resyUserId}
              enabled={openedPanels.has('reservation-history')}
            />
          </Accordion.Item>
          <Accordion.Item id="notify-history" title="Notify History" startElement={<IconBell />}>
            <NotifyHistorySection
              tockPatronId={user.gdusUser?.tockPatronId}
              resyUserId={user.gdusUser?.resyUserId}
              enabled={openedPanels.has('notify-history')}
            />
          </Accordion.Item>
        </Accordion>
      </Box>
      <EditUserDetailsModal
        user={user}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={fetchUserProfile}
      />
      <DeactivateUserModal
        uuid={user.gdusUser?.gdusUuid ?? ''}
        open={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        onSubmit={fetchUserProfile}
      />
      <ReactivateUserModal
        uuid={user.gdusUser?.gdusUuid ?? ''}
        open={isReactivateModalOpen}
        onClose={() => setIsReactivateModalOpen(false)}
        onSubmit={fetchUserProfile}
      />
    </Box>
  );
};

export default UserDetailsRoute;
